import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, Stack, Typography, Chip, Button, IconButton, TextField, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, Avatar, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Menu, MenuItem, Select, FormControl, alpha, useTheme, Snackbar, Alert,
  Skeleton, TablePagination, Autocomplete, Checkbox, CircularProgress, LinearProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Plus, Search, Trash2, Users, CheckCircle2, XCircle, X, UserCheck, MessageCircle, Lock, Upload, FileSpreadsheet, ShieldCheck, Database, Activity, RefreshCw, Edit2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell } from 'recharts';
import { TAG_COLORS, initialsOf, fmtDateTime, WA_TEAL } from './waData';
import WaSectionHeader, { waGridItem, useWaMobile, waDialogProps, waDialogActionsSx } from './WaLayout';
import { waApi } from './waApi';
import { onWaEvent } from './waSocket';
import { useWaSettings } from './WaSettingsContext';

const StatTile = ({ icon: Icon, color, label, value }) => {
  const theme = useTheme();
  return (
    <Card sx={{ 
      p: 2.5, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      height: '100%',
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.15)' : '0 4px 20px rgba(145, 158, 171, 0.05)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: theme.palette.mode === 'dark' ? '0 8px 30px rgba(0,0,0,0.3)' : '0 8px 30px rgba(145, 158, 171, 0.12)',
      }
    }}>
      <Box sx={{ 
        width: 44, 
        height: 44, 
        borderRadius: '14px', 
        bgcolor: alpha(color, 0.12),
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        flexShrink: 0 
      }}>
        <Icon size={20} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, fontFamily: 'Outfit, sans-serif' }}>{value}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600, fontFamily: 'Inter, sans-serif', mt: 0.5 }}>{label}</Typography>
      </Box>
    </Card>
  );
};

const CreateTagDialog = ({ open, onClose, onCreate }) => {
  const mobile = useWaMobile();
  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const submit = () => {
    if (!name.trim()) return;
    onCreate({ name: name.trim(), color });
    setName(''); setColor(TAG_COLORS[0]); onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} {...waDialogProps(mobile, 'xs')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create Tag<IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Tag name" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} autoFocus
            inputProps={{ maxLength: 40 }} helperText={`${name.length}/40`} />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Color</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {TAG_COLORS.map((c) => (
                <Box key={c} onClick={() => setColor(c)} sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                  border: color === c ? '3px solid #fff' : '3px solid transparent', boxShadow: color === c ? `0 0 0 2px ${c}` : 'none' }} />
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim()} onClick={submit}>Create Tag</Button>
      </DialogActions>
    </Dialog>
  );
};

// Import contacts from an Excel/CSV file. Any tags found in the file (a "tags"
// column) plus the optional batch tags typed here are created automatically and
// attached to every imported contact, making them instantly broadcast-ready.
const ImportContactsDialog = ({ open, onClose, onImported, existingTags = [] }) => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const [file, setFile] = useState(null);
  const [batchTags, setBatchTags] = useState([]);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setBatchTags([]); setUpdateExisting(true); setBusy(false); setErr(''); setResult(null); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const submit = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    const tags = batchTags.map((t) => String(t).trim()).filter(Boolean);
    try {
      const res = await waApi.importContacts(file, tags, updateExisting);
      setResult(res);
      onImported(res);
    } catch (e) {
      setErr(e.response?.data?.msg || 'Import failed. Please check the file and try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={close} {...waDialogProps(mobile, 'sm')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Import Contacts<IconButton size="small" onClick={close} disabled={busy}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Stack spacing={1.5} sx={{ py: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircle2 size={20} color={theme.palette.success.main} />
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Import complete</Typography>
            </Stack>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              <b>{result.imported}</b> new contact{result.imported === 1 ? '' : 's'} added.
            </Typography>
            {result.updated > 0 && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                <b>{result.updated}</b> existing contact{result.updated === 1 ? '' : 's'} updated with merged tags and details.
              </Typography>
            )}
            {result.duplicates > 0 && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                <b>{result.duplicates}</b> duplicate number{result.duplicates === 1 ? '' : 's'} skipped (already in your contacts or repeated in the file).
              </Typography>
            )}
            {result.tagsCreated > 0 && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                <b>{result.tagsCreated}</b> new tag{result.tagsCreated === 1 ? '' : 's'} created — now available as broadcast audiences.
              </Typography>
            )}
            {(result.skipped > 0 || result.failed > 0) && (
              <Typography sx={{ fontSize: 12.5, color: 'warning.main' }}>
                {result.skipped > 0 && `${result.skipped} row(s) skipped (missing/invalid phone). `}
                {result.failed > 0 && `${result.failed} row(s) failed.`}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box
              component="label"
              sx={{
                border: `1.5px dashed ${file ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2, p: 2.5, textAlign: 'center', cursor: 'pointer', display: 'block',
                bgcolor: alpha(theme.palette.primary.main, file ? 0.06 : 0),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                hidden
                onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(''); }}
              />
              <Stack alignItems="center" spacing={0.75}>
                <FileSpreadsheet size={26} color={theme.palette.primary.main} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {file ? file.name : 'Click to choose an Excel / CSV file'}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>.xlsx, .xls or .csv (max 10 MB)</Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.08) }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Use column headers <b>name</b>, <b>phone</b> and (optionally) <b>tags</b>. Put multiple tags in
                one cell separated by commas. A phone number is required on every row.
              </Typography>
            </Box>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Update Existing Contacts</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Merge new tags and enrich contact details if number already exists</Typography>
                </Box>
                <Checkbox checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} color="primary" />
              </Stack>
            </Box>

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={existingTags.map((t) => t.name)}
              value={batchTags}
              onChange={(e, val) => setBatchTags(val.map((v) => String(v).trim()).filter(Boolean))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const color = existingTags.find((t) => t.name === option)?.color || theme.palette.primary.main;
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{ bgcolor: alpha(color, 0.14), color, fontWeight: 700 }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tag(s) for all imported contacts"
                  placeholder="Pick an existing tag or type a new one"
                  helperText="Select your tag from the list, or type a name to create a new one."
                />
              )}
            />

            {err && <Alert severity="error">{err}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
        {result ? (
          <Button variant="contained" onClick={close}>Done</Button>
        ) : (
          <>
            <Button onClick={close} color="inherit" disabled={busy}>Cancel</Button>
            <Button variant="contained" disabled={!file || busy} onClick={submit} startIcon={<Upload size={15} />}>
              {busy ? 'Importing…' : 'Import'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const EditContactDialog = ({ open, contact, onClose, onSaved, existingTags = [] }) => {
  const mobile = useWaMobile();
  const theme = useTheme();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [contactTags, setContactTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (contact) {
      setName(contact.name || '');
      setPhone(contact.phone || contact.wa_id || '');
      setOptIn(contact.optIn !== false);
      setContactTags(contact.tags || []);
      setErr('');
    }
  }, [contact]);

  const save = async () => {
    if (!name.trim()) {
      setErr('Contact name is required.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const updated = await waApi.updateContact(contact.id, {
        name: name.trim(),
        phone: phone.trim(),
        opt_in: optIn,
        tags: contactTags,
      });
      onSaved(updated);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.msg || 'Could not update contact.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} {...waDialogProps(mobile, 'sm')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Contact
        <IconButton size="small" onClick={onClose} disabled={busy}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Contact Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <TextField
            label="Phone Number"
            size="small"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy}
            helperText="Include country code (e.g. 919876543210)"
          />
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={existingTags.map((t) => t.name)}
            value={contactTags}
            onChange={(e, val) => setContactTags(val.map((v) => String(v).trim()).filter(Boolean))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const color = existingTags.find((t) => t.name === option)?.color || theme.palette.primary.main;
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ bgcolor: alpha(color, 0.14), color, fontWeight: 700 }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Add or remove tags"
              />
            )}
          />
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.06), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>WhatsApp Broadcast Opt-in</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Contact is eligible to receive marketing broadcasts and automated follow-ups.</Typography>
            </Box>
            <Checkbox checked={optIn} onChange={(e) => setOptIn(e.target.checked)} color="success" />
          </Box>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AuditHealthDialog = ({ open, onClose, auditData, loading, onRunRepair, repairing, onRunDbSync, dbSyncing, onRefresh }) => {
  const theme = useTheme();
  const mobile = useWaMobile();

  return (
    <Dialog open={open} onClose={onClose} {...waDialogProps(mobile, 'md')}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Activity size={20} color={theme.palette.primary.main} />
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Database & WhatsApp Contacts Audit</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={onRefresh} disabled={loading || repairing || dbSyncing} title="Refresh Audit">
            <RefreshCw size={16} />
          </IconButton>
          <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={36} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Auditing database users & WhatsApp contacts…</Typography>
          </Box>
        ) : !auditData ? (
          <Alert severity="error">Could not retrieve audit statistics.</Alert>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <Box sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: theme.palette.success.dark }}>
                  Data Health Status: Synchronised
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                  {auditData.dbLinkedContacts} of {auditData.totalDbUsers} registered database users are linked to WhatsApp.
                </Typography>
              </Box>
              <Chip
                label={`${auditData.totalContacts} Total Contacts`}
                color="success"
                sx={{ fontWeight: 800, height: 26 }}
              />
            </Box>

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>Total Contacts</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25 }}>{auditData.totalContacts}</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <Typography sx={{ fontSize: 11.5, color: 'primary.main', fontWeight: 700 }}>Linked to DB Users</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25, color: 'primary.main' }}>{auditData.dbLinkedContacts}</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                  <Typography sx={{ fontSize: 11.5, color: 'warning.main', fontWeight: 700 }}>Imported / Leads</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25, color: 'warning.main' }}>{auditData.externalContacts}</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>Total DB Users</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25 }}>{auditData.totalDbUsers}</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>Opted-In (Broadcast)</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25, color: 'success.main' }}>{auditData.optedInContacts}</Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card variant="outlined" sx={{ p: 1.75, borderRadius: 2, bgcolor: auditData.healableCount > 0 ? alpha(theme.palette.info.main, 0.08) : undefined }}>
                  <Typography sx={{ fontSize: 11.5, color: 'info.main', fontWeight: 700 }}>Healable Matches</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.25, color: 'info.main' }}>{auditData.healableCount}</Typography>
                </Card>
              </Grid>
            </Grid>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>Account Status Funnel Distribution</Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 2.4 }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha('#22C55E', 0.1), border: '1px solid #22C55E30' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>Verified (Seller)</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#15803d' }}>{auditData.lifecycleBreakdown?.verified || 0}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 2.4 }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha('#F97316', 0.1), border: '1px solid #F9731630' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#c2410c' }}>Products Pending</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#c2410c' }}>{auditData.lifecycleBreakdown?.productsPending || 0}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 2.4 }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha('#F59E0B', 0.1), border: '1px solid #F59E0B30' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>Business Pending</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#b45309' }}>{auditData.lifecycleBreakdown?.businessPending || 0}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 2.4 }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha('#94A3B8', 0.1), border: '1px solid #94A3B830' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Unverified Users</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#475569' }}>{auditData.lifecycleBreakdown?.unverified || 0}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 2.4 }}>
                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha('#1890FF', 0.1), border: '1px solid #1890FF30' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>Open Leads</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0369a1' }}>{auditData.lifecycleBreakdown?.open || 0}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>Reconciliation & Auto-Repair Actions</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
                Syncing bridges database users into WhatsApp contacts while auto-repair links existing unlinked leads to registered user profiles by matching phone numbers.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onRunRepair}
                  disabled={repairing || dbSyncing}
                  startIcon={repairing ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={15} />}
                  sx={{ flex: 1 }}
                >
                  {repairing ? 'Reconciling…' : 'Run Auto-Repair & Reconcile'}
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={onRunDbSync}
                  disabled={repairing || dbSyncing}
                  startIcon={dbSyncing ? <CircularProgress size={14} color="inherit" /> : <Database size={15} />}
                  sx={{ flex: 1 }}
                >
                  {dbSyncing ? 'Syncing…' : 'Sync All DB Users'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
};

const WhatsAppContacts = () => {
  const theme = useTheme();
  const mobile = useWaMobile();
  const { setActiveTab, setActiveConvoId } = useWaSettings();
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  // Aggregate stats from the backend — contacts array reflects loaded slice,
  // while stats reflect full counts across WhatsApp contacts and registered users.
  const [stats, setStats] = useState({ total: 0, optedIn: 0, dbLinked: 0, external: 0, totalDbUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'db' | 'imported'
  const [tagDialog, setTagDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [auditDialog, setAuditDialog] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRepairing, setAuditRepairing] = useState(false);
  const [dbSyncDialog, setDbSyncDialog] = useState(false);
  const [dbSyncBusy, setDbSyncBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState('');
  // Optional explicit severity for the snackbar. When null, severity is inferred
  // from the toast text (legacy behaviour); an explicit value wins so a SUCCESS
  // message that happens to contain words like "invalid"/"skipped" isn't shown red.
  const [toastSeverity, setToastSeverity] = useState(null);
  // Show a toast with an explicit severity (clears any inferred override otherwise).
  const showToast = (msg, severity = null) => { setToastSeverity(severity); setToast(msg); };
  const [rowTagMenu, setRowTagMenu] = useState({ anchorEl: null, contactId: null });
  const [removeTagTarget, setRemoveTagTarget] = useState(null);
  const [deleteContactTarget, setDeleteContactTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Load contacts + tags from the backend.
  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([waApi.getContacts({ limit: 2000 }), waApi.getTags()])
      .then(([cData, tRows]) => {
        const cRows = Array.isArray(cData?.contacts) ? cData.contacts : [];
        setContacts(cRows);
        setStats({
          total: cData?.total ?? cRows.length,
          optedIn: cData?.optedIn ?? cRows.filter((c) => c.optIn).length,
          dbLinked: cData?.dbLinked ?? cRows.filter((c) => c.isDbUser).length,
          external: cData?.external ?? cRows.filter((c) => !c.isDbUser).length,
          totalDbUsers: cData?.totalDbUsers ?? 0,
        });
        if (Array.isArray(tRows)) setTags(tRows);
      })
      .catch(() => setError('Could not load contacts. Check your connection and retry.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Keep the list in sync when lifecycle automation (or another admin) changes a
  // contact's tags/stage — the backend broadcasts wa:contact on every change.
  useEffect(() => onWaEvent('wa:contact', ({ contactId, tags: t, stage, optIn }) => {
    setContacts((prev) => prev.map((c) => (c.id === contactId
      ? { ...c, tags: Array.isArray(t) ? t : c.tags, stage: stage ?? c.stage, optIn: typeof optIn === 'boolean' ? optIn : c.optIn }
      : c)));
  }), []);

  const handleStartChat = async (contactId) => {
    try {
      const res = await waApi.createConversation({ contactId });
      if (res?.conversation) {
        setActiveConvoId(res.conversation.id);
        setActiveTab(0); // Switches to the Inbox tab
      }
    } catch (e) {
      console.error("Failed to start chat from contacts:", e);
    }
  };

  const filtered = useMemo(() => contacts.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.phone && c.phone.includes(q)) ||
      (c.wa_id && c.wa_id.includes(q)) ||
      (c.userInfo?.email && c.userInfo.email.toLowerCase().includes(q)) ||
      (c.userInfo?.name && c.userInfo.name.toLowerCase().includes(q));
    const matchTag = filterTag === 'all' || (c.tags && c.tags.includes(filterTag));
    const matchSource = sourceFilter === 'all' ||
      (sourceFilter === 'db' && c.isDbUser) ||
      (sourceFilter === 'imported' && !c.isDbUser);
    return matchSearch && matchTag && matchSource;
  }), [contacts, search, filterTag, sourceFilter]);

  // Reset to the first page whenever the result set changes, so we never land on
  // a now-empty page after searching/filtering.
  useEffect(() => { setPage(0); setSelected([]); }, [search, filterTag, sourceFilter]);
  const paged = useMemo(() => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filtered, page, rowsPerPage]);

  const tagColor = (name) => tags.find((t) => t.name === name)?.color || theme.palette.grey[500];

  // Contacts-per-tag distribution for the dashboard. Use the backend's whole-book
  // `count` (from listTags); fall back to counting the loaded slice only if the
  // server didn't send one. Counting `contacts` here would under-report, since
  const tagCount = useCallback(
    (t) => (typeof t.count === 'number' ? t.count : contacts.filter((c) => c.tags?.includes(t.name)).length),
    [contacts]
  );
  const tagCounts = useMemo(
    () => tags.map((t) => ({ name: t.name, color: t.color, count: tagCount(t) })),
    [tags, tagCount]
  );
  const [tagViewMode, setTagViewMode] = useState('top'); // 'top' | 'all'

  const sortedTagCounts = useMemo(() => {
    return [...tagCounts].sort((a, b) => b.count - a.count);
  }, [tagCounts]);

  const activeTagCounts = useMemo(() => {
    const active = sortedTagCounts.filter((t) => t.count > 0);
    return active.length > 0 ? active : sortedTagCounts;
  }, [sortedTagCounts]);

  const topChartData = useMemo(() => {
    return activeTagCounts.slice(0, 10).map((t) => ({
      ...t,
      shortName: t.name.length > 22 ? `${t.name.slice(0, 20)}…` : t.name,
    }));
  }, [activeTagCounts]);

  // Add / remove a tag on a single contact row (optimistic; reverts + warns on
  // failure so the UI never silently diverges from the server).
  const addTagToContact = (contactId, tagName) => {
    setContacts((p) => p.map((c) => (c.id === contactId && !c.tags.includes(tagName) ? { ...c, tags: [...c.tags, tagName] } : c)));
    waApi.addContactTag(contactId, tagName).catch(() => {
      setContacts((p) => p.map((c) => (c.id === contactId ? { ...c, tags: c.tags.filter((t) => t !== tagName) } : c)));
      setToast('Could not add the tag. Please try again.');
    });
  };
  const removeTagFromContact = (contactId, tagName) => {
    setContacts((p) => p.map((c) => (c.id === contactId ? { ...c, tags: c.tags.filter((t) => t !== tagName) } : c)));
    waApi.removeContactTag(contactId, tagName).catch(() => {
      setContacts((p) => p.map((c) => (c.id === contactId && !c.tags.includes(tagName) ? { ...c, tags: [...c.tags, tagName] } : c)));
      setToast('Could not remove the tag. Please try again.');
    });
  };

  // Refresh contacts + tags after a spreadsheet import, then summarise the result
  // in a toast. New tags created by the import immediately appear in the Tags
  // panel and the broadcast audience picker.
  const handleImported = (res) => {
    load();
    const parts = [`${res.imported} added`];
    if (res.updated) parts.push(`${res.updated} updated`);
    if (res.duplicates) parts.push(`${res.duplicates} duplicate${res.duplicates === 1 ? '' : 's'} skipped`);
    if (res.tagsCreated) parts.push(`${res.tagsCreated} new tag${res.tagsCreated === 1 ? '' : 's'}`);
    if (res.skipped) parts.push(`${res.skipped} invalid skipped`);
    // Explicitly success — the message can contain "invalid"/"skipped", which the
    // text-based severity inference would otherwise wrongly flag as an error (red).
    showToast(`Import complete — ${parts.join(', ')}.`, 'success');
  };

  // Open Data Audit & Health dialog and fetch live audit metrics
  const openAudit = async () => {
    setAuditDialog(true);
    setAuditLoading(true);
    try {
      const data = await waApi.getContactsAudit();
      setAuditData(data);
    } catch (e) {
      showToast('Could not load audit data. Please try again.', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  // Auto-repair missing links, backfill user names, reconcile tags
  const runAuditRepair = async () => {
    setAuditRepairing(true);
    try {
      const res = await waApi.repairContactsAudit();
      showToast(`Audit & Reconcile complete — ${res.repairedLinks} links repaired, ${res.namesUpdated} names updated, ${res.tagsReconciled} tags reconciled.`, 'success');
      load();
      const data = await waApi.getContactsAudit();
      setAuditData(data);
    } catch (e) {
      showToast(e.response?.data?.msg || 'Audit repair failed. Please try again.', 'error');
    } finally {
      setAuditRepairing(false);
    }
  };

  // Full DB users sync to WhatsApp Contacts
  const runSyncAllDb = async () => {
    setDbSyncBusy(true);
    try {
      const res = await waApi.syncAllDbContacts();
      const parts = [`${res.totalDbUsers} total DB users processed`];
      if (res.created) parts.push(`${res.created} new contacts created`);
      if (res.updated) parts.push(`${res.updated} contacts updated`);
      if (res.skipped) parts.push(`${res.skipped} skipped (no valid phone)`);
      showToast(`DB Sync complete — ${parts.join(', ')}.`, 'success');
      setDbSyncDialog(false);
      load();
      if (auditDialog) {
        const data = await waApi.getContactsAudit();
        setAuditData(data);
      }
    } catch (e) {
      showToast(e.response?.data?.msg || 'Could not sync DB users. Please try again.', 'error');
    } finally {
      setDbSyncBusy(false);
    }
  };

  // Scan the user base and tag everyone with a business + a product as Verified
  // (creating a contact for those not yet in the list). Reloads + summarises.
  const runVerifySync = async () => {
    setVerifyBusy(true);
    try {
      const res = await waApi.syncVerified();
      const parts = [`${res.verified} verified`];
      if (res.created) parts.push(`${res.created} new contact${res.created === 1 ? '' : 's'}`);
      if (res.skipped) parts.push(`${res.skipped} skipped (no phone)`);
      setToast(`Verified sync complete — ${parts.join(', ')}.`);
      setVerifyDialog(false);
      load();
    } catch (e) {
      setToast(e.response?.data?.msg || 'Could not sync verified users. Please try again.');
    } finally {
      setVerifyBusy(false);
    }
  };

  const menuContact = contacts.find((c) => c.id === rowTagMenu.contactId);
  const menuAvailable = menuContact ? tags.filter((t) => !menuContact.tags.includes(t.name)) : [];

  const createTag = async (payload) => {
    try {
      const tag = await waApi.createTag(payload);
      setTags((p) => [...p, tag]);
      setToast('Tag created');
    } catch (e) {
      // Surface the real reason instead of inserting a phantom tag that vanishes
      // on reload. 409 = the tag name already exists; otherwise show the message.
      if (e.response?.status === 409) setToast('A tag with that name already exists.');
      else setToast(e.response?.data?.msg || 'Could not create the tag. Please try again.');
    }
  };

  // Permanently delete a contact (and its chat history) — optimistic, reverts on
  // failure so the row never silently disappears if the server rejects it.
  const confirmDeleteContact = async () => {
    const target = deleteContactTarget;
    if (!target) return;
    const prevContacts = contacts;
    const prevStats = stats;
    const prevTags = tags;

    // Optimistic UI updates
    setContacts((p) => p.filter((c) => c.id !== target.id));
    setStats((s) => ({
      total: Math.max(0, s.total - 1),
      optedIn: Math.max(0, s.optedIn - (target.optIn ? 1 : 0))
    }));
    // Update tag counts in the tags list and graph
    setTags((prev) => prev.map((t) => {
      if (target.tags.includes(t.name)) {
        return { ...t, count: Math.max(0, (t.count || 0) - 1) };
      }
      return t;
    }));
    setSelected((prev) => prev.filter((id) => id !== target.id));
    setDeleteContactTarget(null);

    try {
      await waApi.deleteContact(target.id);
      setToast('Contact deleted');
    } catch (e) {
      setContacts(prevContacts);
      setStats(prevStats);
      setTags(prevTags);
      setToast(e.response?.data?.msg || 'Could not delete the contact. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    setBulkDeleting(true);
    const idsToDelete = [...selected];
    const prevContacts = contacts;
    const prevStats = stats;
    const prevTags = tags;

    // Filter contacts that are to be deleted
    const contactsToDelete = contacts.filter((c) => idsToDelete.includes(c.id));
    const optedInDeletedCount = contactsToDelete.filter((c) => c.optIn).length;

    // Calculate tag decrements
    const tagDecrements = {};
    contactsToDelete.forEach((c) => {
      c.tags.forEach((tagName) => {
        tagDecrements[tagName] = (tagDecrements[tagName] || 0) + 1;
      });
    });

    // Optimistic UI updates
    setContacts((p) => p.filter((c) => !idsToDelete.includes(c.id)));
    setStats((s) => ({
      total: Math.max(0, s.total - idsToDelete.length),
      optedIn: Math.max(0, s.optedIn - optedInDeletedCount)
    }));
    setTags((prev) => prev.map((t) => {
      if (tagDecrements[t.name]) {
        return { ...t, count: Math.max(0, (t.count || 0) - tagDecrements[t.name]) };
      }
      return t;
    }));
    setSelected([]);
    setBulkDeleteConfirm(false);

    try {
      await waApi.deleteContactsBulk(idsToDelete);
      setToast(`Deleted ${idsToDelete.length} contacts`);
    } catch (e) {
      setContacts(prevContacts);
      setStats(prevStats);
      setTags(prevTags);
      setToast(e.response?.data?.msg || 'Could not delete contacts. Please try again.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const confirmDeleteTag = async () => {
    const { id, name } = removeTagTarget || {};
    if (!id) return;
    const prevTags = tags;
    const prevContacts = contacts;
    // Optimistic remove…
    setTags((p) => p.filter((t) => t.id !== id));
    setContacts((p) => p.map((c) => ({ ...c, tags: c.tags.filter((t) => t !== name) })));
    if (filterTag === name) setFilterTag('all'); // don't leave the filter on a deleted tag
    setRemoveTagTarget(null);
    try {
      await waApi.deleteTag(id);
      setToast('Tag deleted');
    } catch (e) {
      // …rolled back if the server rejected it (e.g. a system tag).
      setTags(prevTags);
      setContacts(prevContacts);
      setToast(e.response?.data?.msg || 'Could not delete the tag. Please try again.');
    }
  };

  return (
    <Box>
      <WaSectionHeader
        title="Contacts & Data Management"
        subtitle="Organise everyone you message. Reconcile database registered users and imported contacts for complete data accuracy."
        action={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button variant="outlined" color="primary" startIcon={<Activity size={16} />} onClick={openAudit}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Data Audit & Health
            </Button>
            <Button variant="outlined" color="info" startIcon={<Database size={16} />} onClick={() => setDbSyncDialog(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Sync All DB Users
            </Button>
            <Button variant="contained" startIcon={<Upload size={16} />} onClick={() => setImportDialog(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Import Excel
            </Button>
          </Stack>
        }
      />

      {loading && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i} sx={waGridItem}>
                <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="rounded" width={44} height={44} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="50%" height={26} />
                    <Skeleton width="70%" height={14} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Card sx={{ p: 2 }}>
            <Skeleton width={140} height={20} sx={{ mb: 1.5 }} />
            {Array.from({ length: 5 }).map((_, i) => (
              <Stack key={i} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
                <Skeleton variant="circular" width={34} height={34} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="35%" height={16} />
                  <Skeleton width="25%" height={13} />
                </Box>
                <Skeleton variant="rounded" width={90} height={22} />
              </Stack>
            ))}
          </Card>
        </>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
      <>
      {/* Tag & Source distribution dashboard */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Grid size={{ xs: 6, md: 3 }} sx={waGridItem}>
          <StatTile icon={Users} color={theme.palette.primary.main} label="Total contacts" value={stats.total} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }} sx={waGridItem}>
          <Tooltip title={`${stats.dbLinked || 0} contacts linked to users in database (${stats.totalDbUsers || 0} total registered users)`}>
            <Box sx={{ height: '100%' }}>
              <StatTile icon={Database} color="#0284C7" label={`DB Users (${stats.totalDbUsers || 0} in system)`} value={stats.dbLinked || 0} />
            </Box>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }} sx={waGridItem}>
          <Tooltip title="Contacts imported from Excel or direct leads with no registered DB user yet">
            <Box sx={{ height: '100%' }}>
              <StatTile icon={FileSpreadsheet} color="#F59E0B" label="Imported / Leads" value={stats.external || 0} />
            </Box>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }} sx={waGridItem}>
          <Tooltip title="Opted-in contacts eligible to receive marketing broadcasts">
            <Box sx={{ height: '100%' }}>
              <StatTile icon={UserCheck} color={theme.palette.success.main} label="Opted-in (Broadcast Ready)" value={stats.optedIn || 0} />
            </Box>
          </Tooltip>
        </Grid>
        <Grid size={12} sx={waGridItem}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Contacts per tag</Typography>
                  <Chip label={`${activeTagCounts.length} active tags`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, height: 22, fontSize: 11 }} />
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>Distribution of contacts across tags.</Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={tagViewMode}
                onChange={(e, v) => v && setTagViewMode(v)}
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                  p: 0.5,
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 1.5,
                    border: 'none',
                    '&.Mui-selected': { bgcolor: theme.palette.background.paper, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }
                  }
                }}
              >
                <ToggleButton value="top">Top 10 Chart</ToggleButton>
                <ToggleButton value="all">All Tags ({tagCounts.length})</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {tagCounts.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 4, textAlign: 'center' }}>No tags to chart yet</Typography>
            ) : tagViewMode === 'top' ? (
              <Box sx={{ width: '100%', height: Math.max(220, topChartData.length * 40 + 20) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topChartData} layout="vertical" margin={{ left: 8, right: 30, top: 4, bottom: 4 }} barSize={18}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                    <YAxis
                      type="category"
                      dataKey="shortName"
                      width={mobile ? 110 : 170}
                      tick={{ fontSize: 12, fontWeight: 700, fill: theme.palette.text.primary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <ReTooltip
                      cursor={{ fill: alpha(theme.palette.grey[500], 0.08) }}
                      contentStyle={{ borderRadius: 10, border: `1px solid ${theme.palette.divider}`, fontSize: 12, background: theme.palette.background.paper }}
                      formatter={(v, name, item) => [`${v} contacts`, item?.payload?.name || 'Tag']}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {topChartData.map((t) => <Cell key={t.name} fill={t.color || theme.palette.primary.main} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 1 }}>
                <Grid container spacing={1.5}>
                  {sortedTagCounts.map((t) => {
                    const maxVal = sortedTagCounts[0]?.count || 1;
                    const pct = Math.round(((t.count || 0) / maxVal) * 100);
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.name}>
                        <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color || theme.palette.primary.main, flexShrink: 0 }} />
                              <Tooltip title={t.name}>
                                <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700 }}>{t.name}</Typography>
                              </Tooltip>
                            </Stack>
                            <Chip label={`${t.count} contacts`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 800 }} />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(t.color || theme.palette.primary.main, 0.12),
                              '& .MuiLinearProgress-bar': { bgcolor: t.color || theme.palette.primary.main, borderRadius: 3 }
                            }}
                          />
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
      {/* Tags panel */}
      <Grid size={{ xs: 12, lg: 4 }} sx={waGridItem}>
        <Card sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Tags</Typography>
            <Button size="small" startIcon={<Plus size={14} />} onClick={() => setTagDialog(true)}>New</Button>
          </Stack>
          <Stack spacing={0.75}>
            {tags.map((t) => (
              <Stack key={t.id} direction="row" alignItems="center" spacing={1}
                sx={{ p: 1, borderRadius: 2, '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.06) } }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{t.name}</Typography>
                <Chip label={tagCount(t)} size="small" sx={{ height: 18, fontSize: 10 }} />
                {t.system ? (
                  <Tooltip title="Managed automatically by lifecycle automation — can’t be deleted">
                    <Box sx={{ display: 'flex', px: 0.75, color: 'text.disabled' }}><Lock size={12} /></Box>
                  </Tooltip>
                ) : (
                  <IconButton size="small" aria-label={`Delete tag ${t.name}`} onClick={() => setRemoveTagTarget({ id: t.id, name: t.name })} sx={{ color: 'error.main' }}><Trash2 size={13} /></IconButton>
                )}
              </Stack>
            ))}
            {tags.length === 0 && <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 2, textAlign: 'center' }}>No tags yet</Typography>}
          </Stack>
        </Card>
      </Grid>

      {/* Contacts table */}
      <Grid size={{ xs: 12, lg: 8 }} sx={waGridItem}>
        <Card sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" gap={1} alignItems="center">
            <TextField size="small" placeholder="Search by name, phone, wa_id, email…" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 240 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> }} />
            
            <ToggleButtonGroup
              size="small"
              exclusive
              value={sourceFilter}
              onChange={(e, val) => val && setSourceFilter(val)}
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.08),
                p: 0.25,
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  px: 1.5, py: 0.5, fontSize: 11.5, fontWeight: 700, borderRadius: 1.5, border: 'none',
                  '&.Mui-selected': { bgcolor: theme.palette.background.paper, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }
                }
              }}
            >
              <ToggleButton value="all">All ({contacts.length})</ToggleButton>
              <ToggleButton value="db">DB Users ({stats.dbLinked || 0})</ToggleButton>
              <ToggleButton value="imported">Imported ({stats.external || 0})</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ width: { xs: '100%', md: 150 }, minWidth: { md: 150 } }}>
              <Select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} displayEmpty>
                <MenuItem value="all">All tags</MenuItem>
                {tags.map((t) => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            {selected.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<Trash2 size={16} />}
                onClick={() => setBulkDeleteConfirm(true)}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                Delete Selected ({selected.length})
              </Button>
            )}
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: mobile ? 480 : 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < filtered.length}
                      checked={filtered.length > 0 && selected.length === filtered.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelected(filtered.map((c) => c.id));
                        } else {
                          setSelected([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Contact & Source</TableCell>
                  <TableCell>Tags</TableCell>
                  {!mobile && <TableCell>Opt-in</TableCell>}
                  {!mobile && <TableCell>Linked DB User</TableCell>}
                  {!mobile && <TableCell>Last active</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((c) => (
                  <TableRow key={c.id} hover selected={selected.includes(c.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(c.id)}
                        onChange={() => {
                          setSelected((prev) =>
                            prev.includes(c.id) ? prev.filter((id) => id !== c.id) : [...prev, c.id]
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 800, bgcolor: c.isDbUser ? alpha('#0284C7', 0.9) : alpha(WA_TEAL, 0.85), color: '#fff', fontFamily: 'Outfit, sans-serif' }}>
                          {initialsOf(c.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                            <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>{c.name || 'Unnamed'}</Typography>
                            {c.isDbUser ? (
                              <Tooltip title={c.userInfo?.email ? `DB Account: ${c.userInfo.email} (${c.userInfo.userType || 'Registered'})` : 'Registered user account in database'}>
                                <Chip
                                  label={c.userInfo?.userType || 'DB User'}
                                  size="small"
                                  sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Imported from spreadsheet or inquiry — no registered user account yet">
                                <Chip
                                  label="Imported"
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 9.5, fontWeight: 700, color: 'text.secondary', borderColor: theme.palette.divider }}
                                />
                              </Tooltip>
                            )}
                            {mobile && (
                              c.optIn 
                                ? <CheckCircle2 size={12} style={{ color: theme.palette.success.main }} />
                                : <XCircle size={12} style={{ color: theme.palette.error.main }} />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontFamily: 'Inter, sans-serif' }}>{c.phone}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {c.tags.map((t, i) => (
                          <Chip key={`${t}-${i}`} label={t} size="small" onDelete={() => removeTagFromContact(c.id, t)}
                            sx={{ height: 22, fontSize: 10, bgcolor: alpha(tagColor(t), 0.14), color: tagColor(t), fontWeight: 700,
                              '& .MuiChip-deleteIcon': { color: tagColor(t), fontSize: 14 } }} />
                        ))}
                        <Chip icon={<Plus size={13} />} label="Add tag" size="small" variant="outlined"
                          onClick={(e) => setRowTagMenu({ anchorEl: e.currentTarget, contactId: c.id })}
                          sx={{ height: 22, fontSize: 10, fontWeight: 700, borderStyle: 'dashed', cursor: 'pointer',
                            '& .MuiChip-icon': { ml: 0.5 } }} />
                      </Box>
                    </TableCell>
                    {!mobile && (
                      <TableCell>
                        {c.optIn
                          ? <Tooltip title="Opted in"><CheckCircle2 size={17} color={theme.palette.success.main} /></Tooltip>
                          : <Tooltip title="Not opted in"><XCircle size={17} color={theme.palette.error.main} /></Tooltip>}
                      </TableCell>
                    )}
                    {!mobile && (
                      <TableCell>
                        {c.linkedUser ? (
                          <Tooltip title={c.userInfo?.email ? `${c.userInfo.email} (${c.userInfo.userType || 'User'})` : `Linked DB User ID: ${c.linkedUser}`}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Database size={13} color={theme.palette.primary.main} />
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                                {c.userInfo?.name || c.linkedUser.slice(-6)}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>External Lead</Typography>
                        )}
                      </TableCell>
                    )}
                    {!mobile && (
                      <TableCell><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtDateTime(c.lastActive)}</Typography></TableCell>
                    )}
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit contact details">
                          <IconButton size="small" aria-label={`Edit ${c.name}`}
                            onClick={() => setEditTarget(c)}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
                            <Edit2 size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Start Chat">
                          <IconButton size="small" color="primary" aria-label={`Start chat with ${c.name}`}
                            onClick={() => handleStartChat(c.id)}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
                            <MessageCircle size={17} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete contact">
                          <IconButton size="small" aria-label={`Delete contact ${c.name}`}
                            onClick={() => setDeleteContactTarget(c)}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={mobile ? 4 : 7}>
                    <Stack alignItems="center" sx={{ py: 5, color: 'text.disabled' }} spacing={1}>
                      <Users size={30} /><Typography sx={{ fontSize: 13 }}>No contacts match your filters</Typography>
                    </Stack>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {filtered.length > 0 && (
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={mobile ? '' : 'Rows per page:'}
              sx={{ 
                borderTop: `1px solid ${theme.palette.divider}`,
                ...(mobile ? {
                  '.MuiTablePagination-selectLabel': { display: 'none !important' },
                  '.MuiTablePagination-select': { display: 'none !important' },
                  '.MuiTablePagination-selectIcon': { display: 'none !important' },
                  '.MuiTablePagination-displayedRows': { margin: '0 auto' }
                } : {})
              }}
            />
          )}
        </Card>
      </Grid>
      </Grid>
      </>
      )}

      {/* Per-row "add tag" menu */}
      <Menu anchorEl={rowTagMenu.anchorEl} open={Boolean(rowTagMenu.anchorEl)}
        onClose={() => setRowTagMenu({ anchorEl: null, contactId: null })}>
        {menuAvailable.length === 0 && <MenuItem disabled sx={{ fontSize: 13 }}>All tags added</MenuItem>}
        {menuAvailable.map((t) => (
          <MenuItem key={t.id} sx={{ fontSize: 13 }}
            onClick={() => { addTagToContact(rowTagMenu.contactId, t.name); setRowTagMenu({ anchorEl: null, contactId: null }); }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color, mr: 1 }} />
            {t.name}
          </MenuItem>
        ))}
      </Menu>

      <CreateTagDialog open={tagDialog} onClose={() => setTagDialog(false)} onCreate={createTag} />

      <ImportContactsDialog open={importDialog} onClose={() => setImportDialog(false)} onImported={handleImported} existingTags={tags} />

      {editTarget && (
        <EditContactDialog
          open={Boolean(editTarget)}
          contact={editTarget}
          existingTags={tags}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setContacts((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
            showToast(`Contact “${updated.name}” updated successfully`, 'success');
            load();
          }}
        />
      )}

      <AuditHealthDialog
        open={auditDialog}
        onClose={() => setAuditDialog(false)}
        auditData={auditData}
        loading={auditLoading}
        onRunRepair={runAuditRepair}
        repairing={auditRepairing}
        onRunDbSync={runSyncAllDb}
        dbSyncing={dbSyncBusy}
        onRefresh={openAudit}
      />

      {/* Full DB Users Sync confirmation */}
      <Dialog open={dbSyncDialog} onClose={() => !dbSyncBusy && setDbSyncDialog(false)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Database size={20} color={theme.palette.primary.main} /> Sync All Database Users
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This synchronises <b>all registered users in the database</b> into your WhatsApp contacts list.
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 1.25 }}>
            Each user will be automatically assigned their appropriate account lifecycle status (<b>Verified</b>, <b>Products Pending</b>, <b>Business Pending</b>, or <b>Open</b>). Existing custom tags and chat histories will remain completely intact.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
          <Button onClick={() => setDbSyncDialog(false)} color="inherit" disabled={dbSyncBusy}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={runSyncAllDb} disabled={dbSyncBusy}
            startIcon={dbSyncBusy ? <CircularProgress size={15} color="inherit" /> : <Database size={15} />}>
            {dbSyncBusy ? 'Syncing…' : 'Sync All Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verified sync confirmation */}
      <Dialog open={verifyDialog} onClose={() => !verifyBusy && setVerifyDialog(false)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldCheck size={20} color={theme.palette.success.main} /> Sync Verified Users
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This scans every user and tags anyone who has <b>both a business and at least one product</b> as
            <b> Verified</b>. Qualifying users who aren’t contacts yet are added (when they have a phone number).
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 1.25 }}>
            On a verified contact the <b>Business Register</b> and <b>Products Pending</b> tags are removed — leaving
            just <b>Verified</b> — while any other tags they have stay put.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
          <Button onClick={() => setVerifyDialog(false)} color="inherit" disabled={verifyBusy}>Cancel</Button>
          <Button variant="contained" color="success" onClick={runVerifySync} disabled={verifyBusy}
            startIcon={<ShieldCheck size={15} />}>
            {verifyBusy ? 'Syncing…' : 'Run sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact delete confirmation — removes the contact and its chat history. */}
      <Dialog open={Boolean(deleteContactTarget)} onClose={() => setDeleteContactTarget(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete contact{deleteContactTarget?.name ? ` “${deleteContactTarget.name}”` : ''}?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This permanently removes the contact{deleteContactTarget?.phone ? <> (<b>{deleteContactTarget.phone}</b>)</> : null} and
            their entire chat history from the inbox. This can’t be undone. If they message you again, a fresh contact is created.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
          <Button onClick={() => setDeleteContactTarget(null)} color="inherit">Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteContact} startIcon={<Trash2 size={15} />}>Delete contact</Button>
        </DialogActions>
      </Dialog>

      {/* Tag delete confirmation — deleting a tag strips it from every contact. */}
      <Dialog open={Boolean(removeTagTarget)} onClose={() => setRemoveTagTarget(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete tag “{removeTagTarget?.name}”?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This removes the tag from <b>every contact</b> that has it, and any broadcast audience built on it will no
            longer include those contacts. This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
          <Button onClick={() => setRemoveTagTarget(null)} color="inherit">Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteTag} startIcon={<Trash2 size={15} />}>Delete tag</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk contact delete confirmation */}
      <Dialog open={bulkDeleteConfirm} onClose={() => !bulkDeleting && setBulkDeleteConfirm(false)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete {selected.length} contacts?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This permanently removes the <b>{selected.length}</b> selected contacts and
            their entire chat history from the inbox. This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, ...waDialogActionsSx }}>
          <Button onClick={() => setBulkDeleteConfirm(false)} color="inherit" disabled={bulkDeleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={15} color="inherit" /> : <Trash2 size={15} />}>
            {bulkDeleting ? 'Deleting…' : 'Delete contacts'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => { setToast(''); setToastSeverity(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toastSeverity || (/could not|already exists|error|fail|invalid|unknown|not found|can’t|cannot|required/i.test(toast) ? 'error' : 'success')} variant="filled" onClose={() => { setToast(''); setToastSeverity(null); }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppContacts;
